// The 3-ROW LIST of a home widget (HOME_WIDGETS_SPEC.md §2): a 24 px plate
// (initials / icon / photo), a `Z` 500 title and a right-hand mono meta; an
// optional underline progress under the row and a red dot for a bad row. No
// hairlines — rows separate by gap. The row title is PRIMARY text (quality
// law, HOME_AMBIENT_SKIN_SPEC §3): it wraps and the row grows — never a
// clamp, never an ellipsis (a two-line design, not a two-line cap); the meta
// is short and carries its full text in `title`.
//
//   HomeWidgetList: {
//     items: (el, s) => el.call('crmRecentRows', s.root)
//     // [{ key, title, meta?, metaTail?, initials? | icon? | src?,
//     //    progress? (0..1), bad?, href? | open?: { call, arg } }]
//   }
//
// `metaTail` is the meta's last part when its END names the thing — a host's
// domain ending (".example"). The meta's head then takes the ellipsis and the
// tail never shrinks: "docs.larkf….example", never "docs.larkfield.examp…"
// (the medium Bookmarks widget, media lane on dev 2026-10-25).
//
// A row OPENS ITS RECORD when it says how: `href` makes it a real link to an
// outside page (a new tab), `open: { call, arg }` a real button that runs the
// body's registered function with `arg`; a row with neither stays plain text.
// The frame never answers a click inside the body, so the row's own action is
// the only one. Every row states its tag, so a data id like "b" or "i" is
// never inferred as an HTML element.
//
// The data prop is `items`, NEVER `rows`: `rows` is the smbls grid shorthand
// (grid-template-rows, css-in-props GRID_PROPS), so a `rows` factory is run
// as CSS and the element keeps the function — the list would show skeletons
// forever (measured 2026-09-27).
//
// `items` null = not read yet: three skeleton rows in the same geometry. At
// most `max` rows show — three, the recipe's list; a body whose list is its
// whole content (Next up's day list) may raise it. Shown at L and XL; a list that is the body's ONLY
// visual sets `asVisual: true` and then also takes the visual slot at M (and
// spans the visual + list tracks at L/XL, so no empty track is left).
export const HomeWidgetList = {
  class: 'home-widget-list',
  gridArea: 'list',
  display: 'none',
  flexDirection: 'column',
  gap: 'Y',
  alignSelf: 'start',
  minWidth: '0',
  minHeight: '0',
  asVisual: false,
  items: null,
  max: 3,
  attr: { 'data-as-visual': (el) => (el.asVisual ? 'true' : null) },
  '&[data-as-visual]': { gridArea: 'visual' },
  ':is([data-widget-size="l"]) &': { display: 'flex' },
  ':is([data-widget-size="xl"]) &': { display: 'flex' },
  ':is([data-widget-size="m"]) &[data-as-visual]': { display: 'flex' },
  ':is([data-widget-size="l"]) &[data-as-visual]': { gridRow: 'visual-start / list-end' },
  ':is([data-widget-size="xl"]) &[data-as-visual]': { gridColumn: 'visual-start / list-end' },
  childExtends: 'HomeWidgetRow',
  childrenAs: 'state',
  children: (el) => {
    const max = Math.max(1, Math.floor(Number(el.max)) || 3)
    return Array.isArray(el.items)
      ? el.items.slice(0, max).map((r, i) => ({ ...r, key: String((r && r.key) || 'row' + i) }))
      : [
          { key: 'skel1', skel: true },
          { key: 'skel2', skel: true },
          { key: 'skel3', skel: true }
        ]
  }
}

export const HomeWidgetRow = {
  tag: (el, s) => (s && s.href ? 'a' : s && s.open && s.open.call ? 'button' : 'div'),
  href: (el, s) => (s && s.href) || null,
  target: (el, s) => (s && s.href ? '_blank' : null),
  rel: (el, s) => (s && s.href ? 'noreferrer' : null),
  type: (el, s) => (s && !s.href && s.open && s.open.call ? 'button' : null),
  onClick: (e, el, s) => {
    if (!s || s.href || !s.open || !s.open.call) return
    e.stopPropagation()
    el.call(s.open.call, s.open.arg, e)
  },
  // A link or a button: the house row wash on hover, the global ring on focus.
  width: '100%',
  padding: '0',
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  textAlign: 'left',
  textDecoration: 'none',
  round: 'radiusControl',
  cursor: 'default',
  // Static selectors, not factories: a pseudo block does not re-evaluate.
  '&[href]': { cursor: 'pointer' },
  '&[type="button"]': { cursor: 'pointer' },
  '&[href]:hover': { background: 'hover' },
  '&[type="button"]:hover': { background: 'hover' },
  // A ROW YOU CAN TAP IS 44pt TALL ON A TOUCH SCREEN (DESIGN-SHELL-RESPONSIVE-
  // IOS-SAFARI-1): a widget's list rows measured 23-24px on dev DFI (touch
  // simulated). A pseudo slop would overlap the next row's — a list needs
  // real height — so a row that opens something grows to `controlH`; a
  // static row (no link, no action) keeps the desktop density.
  '@coarse': {
    '&[href]': { minHeight: 'controlH' },
    '&[type="button"]': { minHeight: 'controlH' }
  },
  display: 'grid',
  // The meta column fits its value up to half the row; past that its text
  // ends in an ellipsis (the full value is its tooltip). An `auto` meta
  // grew before the title's 1fr: a bio as meta ("[QA-E] profile bio
  // persistence test", 242px of a 287px row) left the title 0px wide, one
  // letter per line (Fly QA on dev, hr-recent, 2026-10-18).
  gridTemplateColumns: 'auto minmax(0, 1fr) fit-content(50%)',
  gridTemplateAreas: '"lead title meta" "lead bar bar"',
  columnGap: 'Z',
  rowGap: 'X',
  alignItems: 'center',
  minWidth: '0',

  Lead: {
    gridArea: 'lead',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: 'avatarXs',
    flexShrink: '0',
    round: 'radiusPill',
    overflow: 'hidden',
    theme: 'layer',
    fontSize: 'Y',
    fontWeight: '500',
    color: 'caption',
    hide: (el, s) => !s || !(s.initials || s.icon || s.src || s.skel),
    Initials: {
      tag: 'span',
      text: (el, s) => (s && s.initials) || '',
      hide: (el, s) => !s || !s.initials || !!s.src
    },
    Icon: {
      name: (el, s) => (s && s.icon) || 'star',
      boxSize: 'iconSm',
      hide: (el, s) => !s || !s.icon || !!s.src
    },
    Photo: {
      tag: 'img',
      src: (el, s) => (s && s.src) || null,
      alt: (el, s) => (s && s.title) || '',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      hide: (el, s) => !s || !s.src
    }
  },

  // A `mark` row leads with a short bar in the widget hue instead of a plate —
  // Next up's day list (HOME_AMBIENT_SKIN_SPEC.md §4 "hue bar + title + mono
  // time"). The bar is the row's height, the underline progress's thickness.
  Mark: {
    tag: 'span',
    'aria-hidden': 'true',
    gridArea: 'lead',
    alignSelf: 'stretch',
    width: 'W',
    minHeight: 'A',
    round: 'radiusPill',
    background: '--home-widget-accent',
    hide: (el, s) => !s || !s.mark || !!s.skel
  },

  Title: {
    tag: 'span',
    class: 'widget-row-title',
    gridArea: 'title',
    fontSize: 'Z',
    fontWeight: '500',
    lineHeight: '1.35',
    color: 'title',
    minWidth: '0',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.title) || '',
    hide: (el, s) => !!(s && s.skel)
  },

  SkeletonRow: {
    gridArea: 'title',
    width: '60%',
    hide: (el, s) => !(s && s.skel)
  },

  Meta: {
    gridArea: 'meta',
    display: 'flex',
    alignItems: 'center',
    minWidth: '0',
    // The dot keeps its own space: the meta's head and tail are ONE value, so
    // no gap may fall between them.
    Dot: {
      tag: 'span',
      'aria-hidden': 'true',
      boxSize: 'Y',
      flexShrink: '0',
      marginInlineEnd: 'X2',
      round: 'radiusPill',
      background: 'dangerInk',
      hide: (el, s) => !(s && s.bad)
    },
    Text: {
      tag: 'span',
      fontFamily: 'Code',
      fontSize: 'Y',
      fontVariantNumeric: 'tabular-nums',
      color: 'inactive',
      whiteSpace: 'nowrap',
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      title: (el, s) =>
        s && s.meta != null ? String(s.meta) + String((s && s.metaTail) || '') : null,
      text: (el, s) => (s && s.meta != null ? String(s.meta) : ''),
      hide: (el, s) => !s || s.meta == null || s.meta === ''
    },
    Tail: {
      tag: 'span',
      fontFamily: 'Code',
      fontSize: 'Y',
      fontVariantNumeric: 'tabular-nums',
      color: 'inactive',
      whiteSpace: 'nowrap',
      flexShrink: '0',
      text: (el, s) => (s && s.metaTail ? String(s.metaTail) : ''),
      hide: (el, s) => !s || !s.metaTail
    }
  },

  Bar: {
    gridArea: 'bar',
    height: 'W',
    round: 'radiusPill',
    theme: 'layer',
    overflow: 'hidden',
    hide: (el, s) => !s || s.progress == null,
    Fill: {
      height: '100%',
      round: 'radiusPill',
      background: '--home-widget-accent',
      width: (el, s) =>
        Math.round(Math.min(1, Math.max(0, Number(s && s.progress) || 0)) * 1000) / 10 + '%'
    }
  }
}
